import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  Drawer, Row, Select, Spin, Col, Checkbox,
} from 'antd';

import HoverHelp from '../../common/HoverHelp';
import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import { getSourceFieldMap } from './workflowHelpers';

import PropTypes from 'prop-types';

const SOV_DEFAULT_SECTIONS = [
  { sectionKey: 'base', name: 'Base' },
  { sectionKey: 'subcontracts', name: 'Sub-Contracts' },
  { sectionKey: 'changes', name: 'Changes' },
];

const NON_HOLDBACK_CHECKBOX_HELP = 'Adds a row to map an SOV table for lines that exclude retainage. If the project has no holdback rate when the step runs, the default project holdback is turned on.';

export default function WorkflowCreateContractConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  sections = [],
  isDefaultTemplate = false,
  scheduleOfValueTemplateId,
  templateSections = [],
  loadingTemplateSections = false,
  defaultSectionMappings: initialDefault = {},
  templateSectionMappings: initialTemplate = {},
  includeNonHoldback = false,
  onMappingChange,
}) {
  const [defaultSectionMappings, setDefaultSectionMappings] = useState(initialDefault);
  const [templateSectionMappings, setTemplateSectionMappings] = useState(initialTemplate);

  const sortedTemplateSections = useMemo(() => (
    [...templateSections].sort((a, b) => (
      (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
    ))
  ), [templateSections]);

  const mappingRows = useMemo(() => {
    const defaultRows = SOV_DEFAULT_SECTIONS.map(({ sectionKey, name }) => ({
      rowKey: sectionKey,
      label: name,
      type: 'default',
    }));
    if (includeNonHoldback) {
      defaultRows.push({
        rowKey: 'nonHoldback',
        label: 'Non-holdback items',
        type: 'default',
      });
    }
    if (isDefaultTemplate) {
      return defaultRows;
    }
    if (!scheduleOfValueTemplateId) {
      return [];
    }
    const templateRows = sortedTemplateSections.map((section) => ({
      rowKey: section.id,
      label: section.name,
      type: 'template',
    }));
    return [...defaultRows, ...templateRows];
  }, [isDefaultTemplate, scheduleOfValueTemplateId, sortedTemplateSections, includeNonHoldback]);

  const sovTableOptions = useMemo(() => {
    const map = getSourceFieldMap(sections);
    return (map['table - ScheduleOfValues'] || []).map((field) => ({
      label: field.title,
      value: field.id,
    }));
  }, [sections]);

  const onRowTableChange = useCallback((rowKey, type) => (value) => {
    if (type === 'default') {
      const next = { ...defaultSectionMappings };
      if (value) next[rowKey] = value;
      else delete next[rowKey];
      setDefaultSectionMappings(next);
      if (rowKey === 'nonHoldback' && !value) { // If non-holdback is cleared, uncheck the non-holdback checkbox
        onMappingChange({ includeNonHoldback: false, defaultSectionMappings: next, templateSectionMappings });
        return;
      }
      onMappingChange({ defaultSectionMappings: next, templateSectionMappings });
      return;
    }
    const next = { ...templateSectionMappings };
    if (value) next[rowKey] = value;
    else delete next[rowKey];
    setTemplateSectionMappings(next);
    onMappingChange({ defaultSectionMappings, templateSectionMappings: next });
  }, [
    defaultSectionMappings,
    templateSectionMappings,
    onMappingChange,
  ]);

  const hasTemplate = isDefaultTemplate || !!scheduleOfValueTemplateId;

  const onIncludeNonHoldbackChange = useCallback((e) => {
    const checked = e.target.checked;
    if (!checked) {
      const next = { ...defaultSectionMappings };
      delete next.nonHoldback;
      setDefaultSectionMappings(next);
      onMappingChange({ includeNonHoldback: false, defaultSectionMappings: next, templateSectionMappings });
      return;
    }
    onMappingChange({ includeNonHoldback: true });
  }, [defaultSectionMappings, templateSectionMappings, onMappingChange]);

  return (
    <Drawer
      title="Create Contract - mapping"
      width={640}
      onClose={onClose}
      visible={visible}
      destroyOnClose={false}
      maskClosable
      zIndex={1000}
    >
      <Row style={{ marginBottom: 12 }}>
        {isDefaultTemplate ? (
          <span>
            Map <b>default contract areas</b> (Base, Sub-Contracts, Changes) to <b>SOV tables</b> on this form.
          </span>
        ) : (
          <span>
            Map <b>Base</b>, <b>Sub-Contracts</b>, <b>Changes</b>, and any <b>custom template sections</b> you need,
            each to an <b>SOV table</b> on this form.
          </span>
        )}
      </Row>
      {hasTemplate ? (
        null
      ) : (
        <Row style={{ color: '#888', fontSize: 13 }}>
          Select a contract template (or Default) on the workflow step first.
        </Row>
      )}
      <Spin spinning={!isDefaultTemplate && !!scheduleOfValueTemplateId && loadingTemplateSections}>
        {!isDefaultTemplate && scheduleOfValueTemplateId && !loadingTemplateSections && sortedTemplateSections.length === 0 ? (
          <Row style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
            This template has no custom sections; map the default areas below. Add sections in company Schedule of Values templates if you need more.
          </Row>
        ) : null}
        <Row style={{ marginBottom: 8 }} gutter={8} align="middle">
          <Col>
            <Checkbox checked={includeNonHoldback} onChange={onIncludeNonHoldbackChange}>
              Non-holdback items
            </Checkbox>
          </Col>
          <Col>
            <HoverHelp placement="top" content={NON_HOLDBACK_CHECKBOX_HELP} />
          </Col>
        </Row>
        {mappingRows.length > 0 ? (
          <Row style={{ fontWeight: 600, marginBottom: 8 }} gutter={16}>
            <Col span={11}>SOV table (form field)</Col>
            <Col span={13}>Section</Col>
          </Row>
        ) : null}
        {mappingRows.map(({ rowKey, label, type }) => {
          const selectedTableId = type === 'default'
            ? defaultSectionMappings[rowKey]
            : templateSectionMappings[rowKey];
          return (
            <Row
              key={rowKey}
              gutter={16}
              align="middle"
              style={{ marginBottom: 12 }}
            >
              <Col span={11}>
                <Select
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select table"
                  value={selectedTableId || undefined}
                  allowClear
                  onChange={onRowTableChange(rowKey, type)}
                  options={sovTableOptions}
                />
              </Col>
              <Col span={13} style={{ wordBreak: 'break-word' }}>
                {type === 'template' ? (
                  <span>
                    <span style={{ color: '#888', fontSize: 12, marginRight: 6 }}>Template</span>
                    <span style={{ fontWeight: 500 }}>{label}</span>
                  </span>
                ) : (
                  <span>
                    <span style={{ color: '#888', fontSize: 12, marginRight: 6 }}>
                      {rowKey === 'nonHoldback' ? 'Non-holdback' : 'Default'}
                    </span>
                    <span style={{ fontWeight: 500 }}>{label}</span>
                  </span>
                )}
              </Col>
            </Row>
          );
        })}
      </Spin>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
}

WorkflowCreateContractConfigureDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.object),
  isDefaultTemplate: PropTypes.bool,
  scheduleOfValueTemplateId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  templateSections: PropTypes.arrayOf(PropTypes.object),
  loadingTemplateSections: PropTypes.bool,
  defaultSectionMappings: PropTypes.objectOf(PropTypes.string),
  templateSectionMappings: PropTypes.objectOf(PropTypes.string),
  includeNonHoldback: PropTypes.bool,
  onMappingChange: PropTypes.func.isRequired,
};